import moment from "moment";
import camelcaseKeys from "camelcase-keys";
import { useEffect } from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AttributionIcon from "@mui/icons-material/Attribution";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import {
  Box,
  Button,
  Chip,
  Container,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import ManageDialog from "./Manage";
import usePagination from "hooks/pagination";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import ButtonWithConfirm from "components/utils/ButtonWithConfirm";
import { User, useAuth } from "components/auth/AuthProvider";
import LinearIndeterminate from "components/utils/LinearLoader";
import { Offline, Online } from "components/utils/Status";
import { usePavmDisconnect } from "hooks/pavm/base";
import { useUserServiceUsers } from "hooks/user/base";

type UserRowProps = {
  user: User;
  isAdmin: boolean;
  handleDisconnect: (uri: string, email: string, projectId: string) => void;
};

const UserRow = (props: UserRowProps) => {
  const { user: self } = useAuth();
  const { user, isAdmin, handleDisconnect } = props;
  const { email, firstName, lastName, role, project, status } = user;

  const connected = project.connected;
  const connectedId = project.id;
  const connectedIsMe = connected && connectedId === self.project.id;

  const disabled = !connectedIsMe && !(isAdmin && connected);

  const disconnect = () =>
    handleDisconnect(project.uri, user.email, project.id);

  const Status = (
    <Chip
      size="small"
      label={status}
      color={
        status === "active"
          ? "success"
          : status === "pending"
          ? "warning"
          : "error"
      }
    />
  );

  const EmailIcon =
    email === self.email ? (
      <AttributionIcon fontSize="small" color="success" />
    ) : user.session.status === "active" ? (
      Online
    ) : (
      Offline
    );

  const message = (
    <>
      Are you sure you want to disconnect <b>{email}</b> from
      <b> {project.name}</b>? <br />
      This action cannot be undone.
    </>
  );

  return (
    <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <Chip size="small" label={email} icon={EmailIcon} />
      </TableCell>
      <TableCell align="right">{Status}</TableCell>
      <TableCell align="right">{firstName.toLowerCase() || "--"}</TableCell>
      <TableCell align="right">{lastName.toLowerCase() || "--"}</TableCell>
      <TableCell align="right">
        {project.connected ? <b>{project.name}</b> : "--"}
      </TableCell>
      <TableCell align="right">
        {project.connected ? moment(project.updated).fromNow(true) : "--"}
      </TableCell>

      {isAdmin && (
        <TableCell align="right">
          <ManageDialog user={user} />
        </TableCell>
      )}

      <TableCell align="right">
        {connectedIsMe ? (
          <Button color="error" variant="contained" onClick={disconnect}>
            Disconnect
          </Button>
        ) : (
          <ButtonWithConfirm
            type="disconnect"
            label="disconnect"
            color="error"
            message={message}
            disabled={disabled}
            onConfirm={disconnect}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

export const Users = () => {
  const { user: self, setUser } = useAuth();
  const isAdmin = self.role === "admin";

  const paginate = usePagination({ defaultSort: "email", defaultOrder: "asc" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const users = useUserServiceUsers(body);
  const disconnect = usePavmDisconnect();

  const handleDisconnect = (uri: string, email: string, projectId: string) => {
    disconnect.mutate({ uri, email, projectId });
  };

  useEffect(() => {
    if (disconnect.isSuccess) {
      users.refetch();

      const user = camelcaseKeys(disconnect.data.data.user, { deep: true });

      // if you unlock yourself, update user to enure home page reflects accurate 'active project' status
      if (self.email === user.email) setUser(user);
    }
  }, [disconnect.isSuccess]);

  useEffect(() => {
    document.title = "GRACE | users";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Users"
          icon={<PeopleAltIcon />}
          buttons={[
            <TablePagination
              key={"users-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={users?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"users-refresh"}
              onClick={users.refetch}
              isFetching={users.isFetching}
            />,
          ]}
        />

        <TableContainer sx={{ boxShadow: 4, maxHeight: 630 }}>
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow>
                {conf.COLUMNS.USERS.map((column) => (
                  <TableCell
                    key={column.key}
                    sortDirection={order}
                    align={column.isHeader ? "left" : "right"}
                  >
                    <TableSortLabel
                      active={sort === column.key}
                      direction={sort === column.key ? order : "asc"}
                      onClick={(event: React.MouseEvent<unknown>) =>
                        handleSort(event, column.key)
                      }
                    >
                      {column.description ? (
                        <Tooltip title={column.description} placement="top">
                          <b>{column.label}</b>
                        </Tooltip>
                      ) : (
                        <b>{column.label}</b>
                      )}

                      {sort === column.key ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}

                {isAdmin && (
                  <TableCell align="right">
                    <AssignmentIndIcon fontSize="inherit" />
                  </TableCell>
                )}

                <TableCell align="right">
                  <LockOpenOutlinedIcon fontSize="inherit" />
                </TableCell>
              </TableRow>
            </TableHead>

            {users.isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearIndeterminate />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {users?.data?.data?.map((user: User) => (
                  <UserRow
                    key={user.email}
                    user={user}
                    isAdmin={isAdmin}
                    handleDisconnect={handleDisconnect}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Container>
    </ProtectedRoute>
  );
};
